// frontend/src/pages/dashboards/AccessSections.jsx
// Phase 4 step 14d-3: the Share dialog's "Who can open this dashboard" and
// "People" sections (design claude/phase4-14d-sharing-access-design.md
// section 1; brief decisions 4 and 5). Drawn from props only, like
// ShareContent, so each state can be checked as static markup; the requests
// and the state live in ShareDialog.jsx, the rules and words in accessState.js.
//
// Level: two choices. Restricting a dashboard that has working public links
// asks first (the links are revoked for good) and only then sends
// revoke_links. People: one Add box — a colleague from the list (restricted
// dashboards) is added at once, view only; any other address opens a guest
// invitation with the notice that they are outside the organization and the
// filters they may change. Each row: status, Can edit (members, restricted
// only), Resend (guests), Remove (asked inline first).
import React from 'react';
import { Building2, Loader2, Lock, Mail, RotateCw, Send, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  MAX_EMAIL, ROLE_LABELS, guestCapText, guestFiltersText, guestRows, levelOptions, memberRows, removePrompt, resendWait,
  restrictPrompt, rowRights, statusText,
} from './accessState';
import { filterNote, privateCharts } from './shareState';

export const FIELD = 'w-full rounded-xl bg-[var(--ui-inset)] px-3 py-2 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:opacity-60';
export const LABEL = 'mb-1.5 block text-xs font-medium text-[var(--ui-text-muted)]';
export const SECTION = 'text-[13px] font-semibold tracking-[-0.01em] text-[var(--ui-text)]';
const MUTED = 'text-xs text-[var(--ui-text-muted)]';

const Spinner = () => <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />;

/** A section's own error and notice, under what they are about. */
export function Messages({ error = '', notice = '' }) {
  return (
    <>
      {error && <p className="mt-2 text-xs text-red-400" role="alert">{error}</p>}
      {notice && <p className="mt-2 text-xs text-green-400" role="status">{notice}</p>}
    </>
  );
}

// --- who can open it ------------------------------------------------------------------------

export function LevelSection({ access, busy = '', confirm = null, message = {}, onPick, onConfirm, onCancel }) {
  const changing = busy === 'level';
  const org = access.organization;
  return (
    <section className="mt-5" aria-label="Who can open this dashboard" data-level={access.access}>
      <fieldset disabled={Boolean(busy)}>
        <legend className={SECTION}>Who can open this dashboard</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {levelOptions(org).map((o) => {
            const on = access.access === o.id;
            const Icon = o.id === 'org' ? Building2 : Lock;
            return (
              <label key={o.id} data-level-option={o.id}
                className={`flex cursor-pointer items-start gap-2.5 rounded-2xl p-3.5 ring-1 transition-colors ${on ? 'bg-white/[0.07] ring-white/25' : 'bg-[var(--ui-inset)] ring-transparent hover:ring-white/10'}`}>
                <input type="radio" name="share-level" value={o.id} checked={on} onChange={() => onPick(o.id)} className="mt-0.5 h-4 w-4 shrink-0 accent-white" />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-[13px] font-medium">
                    <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> {o.label}
                  </span>
                  <span className="mt-0.5 block text-xs text-[var(--ui-text-muted)]">{o.hint}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {confirm && (
        <div role="alert" className="mt-2.5 rounded-xl bg-red-500/10 p-3 text-xs text-[var(--ui-text)] ring-1 ring-red-500/25" data-restrict-confirm>
          <p>{restrictPrompt(confirm.links)}</p>
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={changing}>Keep it open to {org}</Button>
            <Button type="button" variant="secondary" size="sm" onClick={onConfirm} disabled={changing} className="text-red-300">
              {changing && <Spinner />} {confirm.links === 1 ? 'Restrict and revoke the link' : 'Restrict and revoke the links'}
            </Button>
          </div>
        </div>
      )}
      {changing && !confirm && <p className={`mt-2 flex items-center gap-1.5 ${MUTED}`} role="status"><Spinner /> Saving</p>}
      {access.access === 'restricted' && (
        <p className={`mt-2 ${MUTED}`}>Charts on this dashboard stay visible in Charts to everyone in {org}.</p>
      )}
      <Messages {...message} />
    </section>
  );
}

// --- people ---------------------------------------------------------------------------------

function MemberEdit({ person, rights, inert, busy, pending, onToggleEdit }) {
  if (inert) return null;
  if (rights.toggleEdit) {
    // `pending`: the value just asked for, shown at once and dropped when the server answers (its value then shows).
    return (
      <label className="mr-1 flex items-center gap-1.5 text-xs">
        <input type="checkbox" checked={pending === null || pending === undefined ? person.canEdit : pending} disabled={Boolean(busy)}
          onChange={(e) => onToggleEdit(person, e.target.checked)}
          className="h-4 w-4 accent-white" aria-label={`${person.email} can edit`} />
        Can edit
      </label>
    );
  }
  return (
    <span className={`mr-1 ${MUTED}`} title={rights.lockedReason || (person.canBeEditor ? undefined : 'Only an admin or analyst can be given edit access.')}>
      {person.canEdit ? 'Can edit' : 'View only'}
    </span>
  );
}

export function PersonRow({
  person, access, filters, now, busy = '', confirming = false, pendingEdit = null, onAskRemove, onConfirmRemove, onCancelRemove, onToggleEdit, onResend,
}) {
  const rights = rowRights(person, access);
  const inert = person.kind === 'member' && access.access !== 'restricted';
  const wait = rights.resend ? resendWait(person, access.resendCooldownMinutes, now) : 0;
  const which = guestFiltersText(person, filters);
  const mine = (what) => busy === `${what}:${person.id}`;
  return (
    <li data-person-id={person.id} data-kind={person.kind} data-status={person.status}
      className={`rounded-2xl bg-[var(--ui-inset)] p-3.5 ${inert ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        {/* min-w: on a phone the controls wrap under the address instead of squeezing it to nothing */}
        <div className="min-w-[12rem] flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[13px] font-medium" title={person.email}>{person.email}</span>
            {person.isYou && <span className={`shrink-0 ${MUTED}`}>(you)</span>}
            {person.kind === 'guest' && (
              <span className="shrink-0 rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[11px] font-medium text-blue-300 ring-1 ring-blue-500/20">Guest</span>
            )}
          </div>
          <p className={`mt-0.5 text-xs ${person.status === 'not_sent' ? 'text-amber-300' : 'text-[var(--ui-text-muted)]'}`}>
            {statusText(person, now)}{which ? ` · ${which}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {person.kind === 'member' && (
            <MemberEdit person={person} rights={rights} inert={inert} busy={busy} onToggleEdit={onToggleEdit}
              pending={pendingEdit && pendingEdit.id === person.id ? pendingEdit.value : null} />
          )}
          {mine('edit') && <Spinner />}
          {rights.resend && (
            <Button type="button" variant="secondary" size="sm" onClick={() => onResend(person)} disabled={Boolean(busy) || wait > 0}
              title={wait ? `An invitation can be sent again ${access.resendCooldownMinutes} minutes after the last one.` : undefined}>
              {mine('resend') ? <Spinner /> : <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />}
              {wait ? `Resend in ${wait} min` : 'Resend'}
            </Button>
          )}
          {rights.remove && !confirming && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onAskRemove(person)} disabled={Boolean(busy)}>Remove</Button>
          )}
        </div>
      </div>
      {rights.lockedReason && <p className={`mt-1.5 ${MUTED}`}>{rights.lockedReason}</p>}
      {confirming && (
        <div role="alert" className="mt-2.5 rounded-xl bg-red-500/10 p-3 text-xs text-[var(--ui-text)] ring-1 ring-red-500/25">
          <p>{removePrompt(person)}</p>
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancelRemove} disabled={mine('remove')}>Keep</Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => onConfirmRemove(person)} disabled={mine('remove')} className="text-red-300">
              {mine('remove') && <Spinner />} Remove
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function Suggestions({ suggestions, org, busy, onPick }) {
  const { status, people, truncated } = suggestions;
  if (status === 'error') return <p className={`mt-2 ${MUTED}`}>Colleagues couldn&apos;t be listed. You can still type a full address.</p>;
  if (!people.length) {
    return status === 'loading' ? <p className={`mt-2 flex items-center gap-1.5 ${MUTED}`} role="status"><Spinner /> Looking for colleagues</p> : null;
  }
  return (
    <>
      <ul className="mt-2 flex flex-col gap-0.5 rounded-2xl bg-[var(--ui-inset)] p-1.5" aria-label={`People in ${org}`} data-suggestions>
        {people.map((u) => (
          <li key={u.id}>
            <button type="button" onClick={() => onPick(u)} disabled={u.added || Boolean(busy)}
              className="flex w-full items-center justify-between gap-3 rounded-xl px-2.5 py-1.5 text-left text-[13px] text-[var(--ui-text)] hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent">
              <span className="truncate">{u.email}</span>
              <span className="shrink-0 text-[11px] text-[var(--ui-text-muted)]">{u.added ? 'Added' : ROLE_LABELS[u.role] || ''}</span>
            </button>
          </li>
        ))}
      </ul>
      {truncated && <p className={`mt-1.5 ${MUTED}`}>More people match. Keep typing to narrow the list.</p>}
    </>
  );
}

function GuestDraft({ access, draft, charts, busy, onToggleFilter, onSend, onCancel }) {
  const org = access.organization;
  const cap = guestCapText(access);
  const n = privateCharts(charts).length;
  return (
    <div className="mt-3 rounded-2xl bg-blue-500/[0.07] p-3.5 text-[13px] text-[var(--ui-text)] ring-1 ring-blue-500/20" data-guest-draft>
      <p className="flex gap-2">
        <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-300" aria-hidden="true" />
        <span><strong className="font-medium">{draft.email}</strong> is outside {org}. They will get a sign-in code by email each time they open it.</span>
      </p>
      <p className={`mt-1 pl-[22px] ${MUTED}`}>
        They see this dashboard and nothing else in Connex: every tile and its data{n ? `, including ${n} private chart${n === 1 ? '' : 's'}` : ''}.
      </p>
      {cap ? (
        <p className="mt-2 pl-[22px] text-xs text-amber-300" role="alert">{cap}</p>
      ) : access.filters.length > 0 && (
        <fieldset className="mt-3 pl-[22px]">
          <legend className={LABEL}>Filters they can change</legend>
          <ul className="flex flex-col gap-2">
            {access.filters.map((f) => (
              <li key={f.id}>
                <label className="flex items-start gap-2.5 text-[13px]">
                  <input type="checkbox" checked={draft.filterIds.includes(f.id)} onChange={() => onToggleFilter(f.id)} disabled={Boolean(busy)}
                    className="mt-0.5 h-4 w-4 accent-white" />
                  <span>
                    {f.label}
                    {filterNote(f) && <span className={`block ${MUTED}`}>{filterNote(f).replace('Viewers', 'They')}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className={`mt-2 ${MUTED}`}>A filter you leave unticked keeps its starting value for them.</p>
        </fieldset>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy === 'add'}>Cancel</Button>
        {!cap && (
          <Button type="button" size="sm" onClick={onSend} disabled={Boolean(busy)}>
            {busy === 'add' ? <Spinner /> : <Send className="h-3.5 w-3.5" aria-hidden="true" />} Send invitation
          </Button>
        )}
      </div>
    </div>
  );
}

function AlreadyMember({ access, already, busy, onAddInstead, onCancel }) {
  const restricted = access.access === 'restricted';
  return (
    <div role="alert" className="mt-3 rounded-2xl bg-white/[0.05] p-3.5 text-[13px] text-[var(--ui-text)]" data-already-member>
      <p>
        <strong className="font-medium">{already.email}</strong> belongs to someone in {access.organization}.
        {restricted ? ' Add them as a person: they sign in as usual, no code needed.' : ' They can already open this dashboard.'}
      </p>
      <div className="mt-2 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy === 'add'}>{restricted && already.user ? 'Cancel' : 'OK'}</Button>
        {restricted && already.user && (
          <Button type="button" size="sm" onClick={() => onAddInstead(already.user)} disabled={Boolean(busy)}>
            {busy === 'add' ? <Spinner /> : <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />} Add as a person instead
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * ui: { entry, suggestions: { status: 'idle' | 'loading' | 'ready' | 'error', people (shownSuggestions), truncated },
 *       problem, guestDraft, already: { email, user } | null, removing: person id | null,
 *       pendingEdit: { id, value } | null (a Can edit change on its way) }
 */
export function PeopleSection({
  access, charts = [], now = Date.now(), busy = '', message = {}, ui,
  onEntry, onSubmitEntry, onPickSuggestion, onToggleGuestFilter, onSendGuest, onCancelGuest, onAddInstead,
  onAskRemove, onConfirmRemove, onCancelRemove, onToggleEdit, onResend,
}) {
  const org = access.organization;
  const restricted = access.access === 'restricted';
  const members = memberRows(access);
  const guests = guestRows(access);
  const rows = restricted ? [...members, ...guests] : [...guests, ...members];   // on the org level the named colleagues are inert: last
  const typing = Boolean(ui.entry.trim()) && !ui.guestDraft && !ui.already;
  const g = access.guests;
  return (
    <section className="mt-6" aria-label="People">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className={SECTION}>People</h3>
        {g.active > 0 && <span className={MUTED}>{g.active} of {g.max} guests</span>}
      </div>
      <p className={`mt-1 ${MUTED}`}>
        {restricted
          ? `Add colleagues from ${org}, or invite anyone else by email.`
          : `Everyone in ${org} can already open it. Invite people outside ${org} by email.`}
      </p>

      <form className="mt-3" aria-label="Add people" onSubmit={(e) => { e.preventDefault(); onSubmitEntry(); }}>
        <label className={LABEL} htmlFor="share-person">{restricted ? 'Add people' : `Invite someone outside ${org}`}</label>
        <div className="flex gap-2">
          <input id="share-person" type="text" inputMode="email" autoComplete="off" spellCheck={false} maxLength={MAX_EMAIL}
            value={ui.entry} onChange={(e) => onEntry(e.target.value)} disabled={busy === 'add' || Boolean(ui.guestDraft)}
            placeholder={restricted ? 'A colleague’s email, or anyone’s' : 'name@company.com'} className={`${FIELD} min-w-0 flex-1`} />
          <Button type="submit" size="sm" disabled={!ui.entry.trim() || Boolean(busy) || Boolean(ui.guestDraft)}>
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" /> Add
          </Button>
        </div>
        {restricted && typing && <Suggestions suggestions={ui.suggestions} org={org} busy={busy} onPick={onPickSuggestion} />}
        {ui.problem && <p className="mt-2 text-xs text-amber-300" role="alert">{ui.problem}</p>}
      </form>

      {ui.guestDraft && (
        <GuestDraft access={access} draft={ui.guestDraft} charts={charts} busy={busy}
          onToggleFilter={onToggleGuestFilter} onSend={onSendGuest} onCancel={onCancelGuest} />
      )}
      {ui.already && <AlreadyMember access={access} already={ui.already} busy={busy} onAddInstead={onAddInstead} onCancel={onCancelGuest} />}
      <Messages {...message} />

      {rows.length === 0 ? (
        <p className={`mt-3 ${MUTED}`}>{restricted ? 'Nobody is named yet. Admins can always open it.' : 'No guests yet.'}</p>
      ) : (
        <>
          {!restricted && members.length > 0 && (
            <p className={`mt-3 ${MUTED}`}>The colleagues named here can open it anyway, as everyone in {org} can. They keep their place if you restrict it again.</p>
          )}
          <ul className="mt-3 flex flex-col gap-2" aria-label="People with access">
            {rows.map((p) => (
              <PersonRow key={p.id} person={p} access={access} filters={access.filters} now={now} busy={busy}
                confirming={ui.removing === p.id} pendingEdit={ui.pendingEdit || null} onAskRemove={onAskRemove} onConfirmRemove={onConfirmRemove} onCancelRemove={onCancelRemove}
                onToggleEdit={onToggleEdit} onResend={onResend} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
